import {useEffect, useMemo, useState} from 'react';
import {defineMessages, useIntl} from 'react-intl';
import {distinctUntilChanged, map, Subscription, takeWhile} from 'rxjs';
import {
  ClientStatus,
  Conversation,
  ConversationLogItem,
  ConversationStatus,
} from '@stripe-internal/conversations-client';
import type {Attachment} from '@stripe-internal/conversations-client';

import {useAnalytics} from '../AnalyticsProvider';
import {Input} from '../Chat/Input';
import type {InputComponent, InputProps} from '../Chat/InputProps';
import {ConnectionNotice} from '../Chat/ConnectionNotice';
import {isAssistantEntryPoint} from '../StripeAssistant/analytics-utils';
import {
  useUniversalHelpEncourageMoreDetailFromUserMse5042Enabled,
  useNewUniversalHelpDesign,
} from '../FeatureFlagsProvider';
import {InputWithRotatingPlaceholders} from '../Chat/InputWithRotatingPlaceholders';
import {InputWithTransitioningPlaceholder} from '../Chat/InputWithTransitioningPlaceholder';
import {useFileStaging} from '../FileStaging/useFileStaging';

export const messages = defineMessages({
  emptyPlaceholder: {
    id: 'conversations_ui.conversation.chatInput.emptyPlaceholder',
    description:
      'Label for the chat textbox when no messages have been sent yet',
    defaultMessage: 'How can we help?',
  },
  replyPlaceholder: {
    id: 'conversations_ui.conversation.chatInput.replyPlaceholder',
    description: 'Label for the chat textbox when there is an ongoing chat',
    defaultMessage: 'Write a reply…',
  },
  promptPlaceholder: {
    id: 'conversations_ui.conversation.chatInput.promptPlaceholder',
    description:
      'Label for the chat textbox when the user has the option to type out their question against a chatbot',
    defaultMessage: 'Ask a question',
  },
  encourageMoreDetailExperimentPromptDisputePlaceholder: {
    id: 'conversations_ui.conversation.chatInput.encourageMoreDetailExperiment.promptDisputePlaceholder',
    description:
      'Label for the chat textbox when the user has the option to type out their question against a chatbot',
    defaultMessage: 'How long does it take to receive a verdict on a dispute?',
  },
  encourageMoreDetailExperimentPromptRefundPlaceholder: {
    id: 'conversations_ui.conversation.chatInput.encourageMoreDetailExperiment.promptRefundPlaceholder',
    description:
      'Label for the chat textbox when the user has the option to type out their question against a chatbot',
    defaultMessage: 'Can I refund a successful transaction from the past week?',
  },
  encourageMoreDetailExperimentPromptPaymentMethodsPlaceholder: {
    id: 'conversations_ui.conversation.chatInput.encourageMoreDetailExperiment.promptPaymentMethodsPlaceholder',
    description:
      'Label for the chat textbox when the user has the option to type out their question against a chatbot',
    defaultMessage: 'How do I activate new payment methods?',
  },
  encourageMoreDetailExperimentPromptOnboardingPlaceholder: {
    id: 'conversations_ui.conversation.chatInput.encourageMoreDetailExperiment.promptOnboardingPlaceholder',
    description:
      'Label for the chat textbox when the user has the option to type out their question against a chatbot',
    defaultMessage: 'What is Stripe-hosted onboarding for connected accounts?',
  },
  agentAwarenessPlaceholder: {
    id: 'conversations_ui.conversation.chatInput.agentAwarenessPlaceholder',
    description:
      'Label for the chat textbox when the user is in the agent awareness placeholder experiment treatment',
    defaultMessage: 'Ask a question, or connect with a support agent',
  },
  escalationMessage: {
    id: 'conversations_ui.conversation.chatInput.escalationMessage',
    description:
      'Message sent when the user clicks the escape hatch button to escalate to a human agent',
    defaultMessage: 'Contact support agent',
  },
});

const logContainsUserMessage = (items: ConversationLogItem[]): boolean => {
  return items.some(
    (item: ConversationLogItem) =>
      item.name === 'conversation-message-added' &&
      item.message.participant.role === 'user',
  );
};

const logContainsAgentMessages = (items: ConversationLogItem[]): boolean => {
  return items.some(
    (item: ConversationLogItem) =>
      item.name === 'conversation-message-added' &&
      item.message.participant.role === 'agent',
  );
};

/**
 * When a conversation is started, there's an option menu sent that
 * lists the user as the participant, even though it was bot-sent.
 * This function checks for any user-sent messages, taking that
 * edge case into account and making sure the user actually interacted.
 */
export const logContainsUserEngagement = (
  items: ConversationLogItem[],
): boolean => {
  return items.some((item: ConversationLogItem) => {
    if (item.name !== 'conversation-message-added') return false;

    if (
      item.message.participant.role === 'user' &&
      item.message.type === 'text-conversation-message'
    ) {
      return true;
    }

    if (
      item.message.type === 'button-options-conversation-message' &&
      item.message.selectedOptionId !== undefined
    ) {
      return true;
    }

    return false;
  });
};

export const logContainsBotMessage = (
  items: ConversationLogItem[],
): boolean => {
  const filtered = items.filter((item) => {
    if (
      item.name === 'conversation-message-added' &&
      item.message.participant.role === 'bot'
    ) {
      return true;
    } else if ('participant' in item && item.participant.role === 'bot') {
      return true;
    }
    return false;
  });

  return filtered.length > 0;
};

const logContainsAgentAwarenessPlaceholderVariant = (
  items: ConversationLogItem[],
): boolean => {
  return items.some(
    (item: ConversationLogItem) =>
      item.name === 'conversation-message-added' &&
      item.message.type === 'button-options-conversation-message' &&
      item.message.experimentVariant === 'agent_awareness_placeholder',
  );
};

const isSupportChannelMenuLogItem = (item: ConversationLogItem): boolean => {
  if (item.name !== 'conversation-message-added') return false;

  const {message} = item;
  return (
    (message.type === 'inline-data-conversation-message' &&
      message.inlineData.name === 'universal_help_channel_menu') ||
    (message.type === 'custom-part-conversation-message' &&
      message.part.type === 'channel_menu')
  );
};

/**
 * Returns true if the escape hatch button should be shown based on the log.
 * The backend sends the first_request_agent variant exactly once per conversation.
 * Show when: escape hatch exists AND the backend has not shown the channel menu.
 * Hide when: the backend has shown the channel menu triggered by escalation.
 */
export const logShouldShowEscapeHatch = (
  items: ConversationLogItem[],
): boolean => {
  // Backend sends first_request_agent exactly once when the user's first message is an escalation attempt.
  const experimentEnabled = items.some(
    (item) =>
      item.name === 'conversation-message-added' &&
      item.message.type === 'button-options-conversation-message' &&
      item.message.experimentVariant === 'first_request_agent',
  );

  if (!experimentEnabled) return false;

  const hasSupportChannel = items.some(isSupportChannelMenuLogItem);

  return !hasSupportChannel;
};

export const usePlaceholderValue = (
  conversation: Conversation,
):
  | {placeholder: string}
  | {placeholders: string[]}
  | {
      transitionPlaceholder: {
        initial: string;
        final: string;
        triggered: boolean;
      };
    } => {
  const [agentHasSentMessage, setAgentHasSentMessage] = useState<boolean>(
    logContainsAgentMessages(conversation.log.get()),
  );

  const [hasBotMessageAndUserEngagement, setHasBotMessageAndUserEngagement] =
    useState<boolean>(
      logContainsBotMessage(conversation.log.get()) &&
        logContainsUserEngagement(conversation.log.get()),
    );

  const [hasAgentAwarenessPlaceholder, setHasAgentAwarenessPlaceholder] =
    useState<boolean>(
      logContainsAgentAwarenessPlaceholderVariant(conversation.log.get()),
    );

  useEffect(() => {
    const sub = conversation.log
      .observable()
      .pipe(
        takeWhile(() => !agentHasSentMessage),
        map(logContainsAgentMessages),
      )
      .subscribe(setAgentHasSentMessage);
    return () => sub.unsubscribe();
  }, [conversation, agentHasSentMessage]);

  useEffect(() => {
    const sub = conversation.log
      .observable()
      .pipe(
        takeWhile(() => !hasBotMessageAndUserEngagement),
        map((v) => logContainsUserEngagement(v) && logContainsBotMessage(v)),
      )
      .subscribe(setHasBotMessageAndUserEngagement);
    return () => sub.unsubscribe();
  }, [conversation, hasBotMessageAndUserEngagement]);

  useEffect(() => {
    const sub = conversation.log
      .observable()
      .pipe(
        takeWhile(() => !hasAgentAwarenessPlaceholder),
        map(logContainsAgentAwarenessPlaceholderVariant),
      )
      .subscribe(setHasAgentAwarenessPlaceholder);
    return () => sub.unsubscribe();
  }, [conversation, hasAgentAwarenessPlaceholder]);

  const intl = useIntl();
  const isEncourageMoreDetailFlagEnabled =
    useUniversalHelpEncourageMoreDetailFromUserMse5042Enabled();

  const shuffledPlaceholders = useMemo(() => {
    return [
      messages.encourageMoreDetailExperimentPromptDisputePlaceholder,
      messages.encourageMoreDetailExperimentPromptRefundPlaceholder,
      messages.encourageMoreDetailExperimentPromptPaymentMethodsPlaceholder,
      messages.encourageMoreDetailExperimentPromptOnboardingPlaceholder,
    ]
      .map((message) => intl.formatMessage(message))
      .map((message) => ({message, sort: Math.random()}))
      .sort((a, b) => a.sort - b.sort)
      .map(({message}) => message);
  }, [intl]);

  if (conversation.context.channel === 'bot') {
    if (isEncourageMoreDetailFlagEnabled && !hasBotMessageAndUserEngagement) {
      return {
        placeholders: shuffledPlaceholders,
      };
    }
    return {
      transitionPlaceholder: {
        initial: intl.formatMessage(messages.promptPlaceholder),
        final: intl.formatMessage(messages.agentAwarenessPlaceholder),
        triggered: hasAgentAwarenessPlaceholder,
      },
    };
  } else {
    // if in an actual human-to-human interaction, display empty vs reply depending on if the agent has replied
    return agentHasSentMessage
      ? {placeholder: intl.formatMessage(messages.replyPlaceholder)}
      : {placeholder: intl.formatMessage(messages.emptyPlaceholder)};
  }
};

export type ChatInputProps = {
  conversation: Conversation;
  attachmentsDisabled?: boolean;
  attachmentsUseStaging?: boolean;
  hidden?: boolean;
  pending?: boolean;
  animate?: boolean;
  focusInput?: boolean;
  InputComponent?: InputComponent;
  newDesignEnabled?: boolean;
};

export const ChatInput = ({
  conversation,
  attachmentsDisabled = false,
  attachmentsUseStaging = false,
  hidden = false,
  pending = false,
  animate = true,
  focusInput,
  InputComponent: InputComponentOverride = Input,
  newDesignEnabled,
}: ChatInputProps) => {
  const analytics = useAnalytics();
  const newDesignFeatureFlagEnabled = useNewUniversalHelpDesign();
  const useNewDesign = newDesignEnabled ?? newDesignFeatureFlagEnabled;

  const [conversationStatus, setConversationStatus] =
    useState<ConversationStatus>(conversation.status);
  const [clientStatus, setClientStatus] = useState<ClientStatus>(
    conversation.client.getStatus(),
  );

  const [userHasSentMessage, setUserHasSentMessage] = useState<boolean>(
    logContainsUserMessage(conversation.log.get()),
  );

  const [showEscapeHatch, setShowEscapeHatch] = useState<boolean>(
    logShouldShowEscapeHatch(conversation.log.get()),
  );
  const [escapeHatchClicked, setEscapeHatchClicked] = useState<boolean>(false);

  const intl = useIntl();

  const placeholderResult = usePlaceholderValue(conversation);
  const {stagedFiles, stageFile, unstageFile, clearStaged, uploadErrors} =
    useFileStaging('universal_help_attachment');
  const isBotContext = conversation.context.channel === 'bot';
  const useStaging = isBotContext || attachmentsUseStaging;

  useEffect(() => {
    const sub = new Subscription();
    sub.add(conversation.statusObservable().subscribe(setConversationStatus));
    sub.add(conversation.client.statusObservable().subscribe(setClientStatus));
    return () => sub.unsubscribe();
  }, [conversation]);

  useEffect(() => {
    setShowEscapeHatch(logShouldShowEscapeHatch(conversation.log.get()));
    setEscapeHatchClicked(false);
  }, [conversation]);

  useEffect(() => {
    const sub = conversation.log
      .observable()
      .pipe(
        takeWhile(() => !userHasSentMessage),
        map(logContainsUserMessage),
      )
      .subscribe(setUserHasSentMessage);
    return () => sub.unsubscribe();
  }, [conversation, userHasSentMessage]);

  useEffect(() => {
    const sub = conversation.log
      .observable()
      .pipe(map(logShouldShowEscapeHatch), distinctUntilChanged())
      .subscribe(setShowEscapeHatch);
    return () => sub.unsubscribe();
  }, [conversation]);

  if (conversationStatus !== ConversationStatus.Active && !useNewDesign) {
    return null;
  }

  if (
    clientStatus === ClientStatus.Offline ||
    clientStatus === ClientStatus.Reconnecting
  ) {
    return <ConnectionNotice status="reconnecting" />;
  }
  const inputDisabled = clientStatus !== ClientStatus.Connected;

  const handleSendMessage = async (message: string) => {
    if (useStaging && stagedFiles.length > 0) {
      const attachments: Attachment[] = stagedFiles
        .filter((stagedFile) => stagedFile.status === 'uploaded')
        .map((stagedFile) => ({
          name: stagedFile.file.name,
          size: stagedFile.file.size,
          type: stagedFile.file.type,
          upload_token: stagedFile.uploadToken,
        }));
      const msg = conversation.buildTextWithAttachmentsMessage(
        message,
        attachments,
      );
      clearStaged();
      await conversation.sendMessage(msg);
      if (attachments.length > 0) {
        analytics.action('universal_help_attachment_message_sent', {
          attachmentCount: attachments.length,
          hasText: message.trim() !== '',
        });
      }
    } else {
      if (message.trim() === '') {
        return;
      }
      const msg = conversation.buildTextMessage(message);
      await conversation.sendMessage(msg);
    }

    if (isAssistantEntryPoint(conversation)) {
      analytics.action('ai_assistant_message_sent', {
        ai_assistant_message_content: message,
      });
    }
  };

  const handleSendFile = async (file: File) => {
    if (useStaging) {
      stageFile(file);
    } else {
      const msg = conversation.buildAttachmentMessage(file);
      await conversation.sendMessage(msg);
    }
  };

  const handleSendTyping = () => {
    conversation.sendTyping();
  };

  const handleEscalateToHuman = async () => {
    analytics.action('universal_help_escape_hatch_clicked');
    setEscapeHatchClicked(true);
    const msg = conversation.buildTextMessage(
      intl.formatMessage(messages.escalationMessage),
    );
    await conversation.sendMessage(msg);
  };

  const props: InputProps = {
    disabled: inputDisabled,
    pending,
    onSendMessage: handleSendMessage,
    onSendFile: handleSendFile,
    onSendTyping: handleSendTyping,
    attachmentDisabled:
      attachmentsDisabled || (!useStaging && !userHasSentMessage),
    stagedFiles,
    onRemoveStagedFile: unstageFile,
    uploadErrors,
    hidden,
    animate,
    focusInput,
    onEscalateToHuman:
      isBotContext && showEscapeHatch && !escapeHatchClicked
        ? handleEscalateToHuman
        : undefined,
  };

  // this would only be true if isEncourageMoreDetailFlagEnabled is true
  if ('placeholders' in placeholderResult) {
    return (
      <InputWithRotatingPlaceholders
        InputComponent={InputComponentOverride}
        placeholders={placeholderResult.placeholders}
        {...props}
      />
    );
  }

  // ensure smooth transition between the original placeholder to the agent
  // awareness placeholder when the triggered condition is met
  if ('transitionPlaceholder' in placeholderResult) {
    return (
      <InputWithTransitioningPlaceholder
        InputComponent={InputComponentOverride}
        initialPlaceholder={placeholderResult.transitionPlaceholder.initial}
        finalPlaceholder={placeholderResult.transitionPlaceholder.final}
        triggered={placeholderResult.transitionPlaceholder.triggered}
        {...props}
      />
    );
  }

  return (
    <InputComponentOverride
      placeholder={placeholderResult.placeholder}
      {...props}
    />
  );
};
